<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const isLoading = ref(true);
const isRefreshing = ref(false);
const errorMessage = ref('');
const status = ref(null);
const logoUrl = 'https://activitymessenger.com/wp-content/themes/activitymessenger/images/Logo.svg';
let refreshTimer = null;

const appElement = document.getElementById('app');
const supportedLocales = ['en', 'fr'];
const queryLocale = new URLSearchParams(window.location.search).get('lang');
const initialLocale = supportedLocales.includes(queryLocale)
    ? queryLocale
    : supportedLocales.includes(appElement?.dataset.locale)
        ? appElement.dataset.locale
        : 'en';
const currentLocale = ref(initialLocale);

const translations = {
    en: {
        title: 'ActivityMessenger Status',
        metaDescription: 'Activity Messenger production status',
        checkingProduction: 'Checking production...',
        loadError: 'Status data could not be loaded.',
        currentOverview: 'Current overview',
        servicesOperational: 'Services operational',
        checkInterval: 'Check interval',
        averageResponse: 'Avg response',
        lastChecked: 'Last checked',
        liveServices: 'Live services',
        maintenanceActive: 'Maintenance in progress',
        maintenanceUpcoming: 'Scheduled maintenance',
        maintenanceWindow: 'Window',
        maintenanceNoEnd: 'No planned end date',
        maintenanceServices: 'Affects',
        refresh: 'Refresh',
        refreshing: 'Refreshing',
        languageToggleLabel: 'Display language',
        english: 'English',
        french: 'French',
        secondsUnit: 'sec',
        checkingNow: 'Checking now',
        lastCheckedLessThanMinute: 'Last checked less than 1 minute ago',
        lastCheckedMinutes(minutes) {
            return `Last checked ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
        },
        statusLabels: {
            operational: 'Operational',
            degraded: 'Degraded',
            maintenance: 'Under maintenance',
            major_outage: 'Down',
            unknown: 'Unknown',
        },
        overallLabels: {
            operational: 'All systems operational',
            degraded: 'Some services degraded',
            maintenance: 'Maintenance in progress',
            major_outage: 'Service disruption detected',
            unknown: 'Some checks unavailable',
        },
        serviceNames: {
            Website: 'Website',
            'Web App': 'Web App',
            API: 'API',
            SMS: 'SMS',
            Email: 'Email',
            Payments: 'Payments',
            Database: 'Database',
        },
    },
    fr: {
        title: 'Statut ActivityMessenger',
        metaDescription: 'Statut de production ActivityMessenger',
        checkingProduction: 'Vérification de la production...',
        loadError: 'Les données de statut n’ont pas pu être chargées.',
        currentOverview: 'Vue actuelle',
        servicesOperational: 'Services opérationnels',
        checkInterval: 'Intervalle',
        averageResponse: 'Réponse moyenne',
        lastChecked: 'Dernière vérification',
        liveServices: 'Services en direct',
        maintenanceActive: 'Maintenance en cours',
        maintenanceUpcoming: 'Maintenance planifiée',
        maintenanceWindow: 'Fenêtre',
        maintenanceNoEnd: 'Aucune date de fin prévue',
        maintenanceServices: 'Affecte',
        refresh: 'Actualiser',
        refreshing: 'Actualisation',
        languageToggleLabel: 'Langue d’affichage',
        english: 'Anglais',
        french: 'Français',
        secondsUnit: 'sec',
        checkingNow: 'Vérification en cours',
        lastCheckedLessThanMinute: 'Dernière vérification il y a moins d’une minute',
        lastCheckedMinutes(minutes) {
            return `Dernière vérification il y a ${minutes} minute${minutes === 1 ? '' : 's'}`;
        },
        statusLabels: {
            operational: 'Opérationnel',
            degraded: 'Dégradé',
            maintenance: 'En maintenance',
            major_outage: 'Hors service',
            unknown: 'Inconnu',
        },
        overallLabels: {
            operational: 'Tous les systèmes sont opérationnels',
            degraded: 'Certains services sont dégradés',
            maintenance: 'Maintenance en cours',
            major_outage: 'Interruption de service détectée',
            unknown: 'Certaines vérifications sont indisponibles',
        },
        serviceNames: {
            Website: 'Site web',
            'Web App': 'Application web',
            API: 'API',
            SMS: 'SMS',
            Email: 'Courriel',
            Payments: 'Paiements',
            Database: 'Base de données',
        },
    },
};

const copy = computed(() => translations[currentLocale.value] ?? translations.en);

const statusIcons = {
    operational: '✓',
    degraded: '~',
    maintenance: 'i',
    major_outage: '!',
    unknown: '?',
};

const checks = computed(() => status.value?.checks ?? []);
const activeMaintenances = computed(() => status.value?.maintenances?.active ?? []);
const upcomingMaintenances = computed(() => status.value?.maintenances?.upcoming ?? []);
const hasMaintenances = computed(() => activeMaintenances.value.length > 0 || upcomingMaintenances.value.length > 0);
const serviceOrder = ['Website', 'Web App', 'API', 'SMS', 'Email', 'Payments', 'Database'];
const liveServices = computed(() => [...checks.value].sort((a, b) => {
    const left = serviceOrder.indexOf(a.name);
    const right = serviceOrder.indexOf(b.name);

    return (left === -1 ? 99 : left) - (right === -1 ? 99 : right);
}));
const displayServiceName = (name) => copy.value.serviceNames[name] ?? name;

const serviceIcon = (name) => {
    const key = name.toLowerCase();

    if (key === 'website') {
        return 'website';
    }

    if (key === 'web app') {
        return 'web-app';
    }

    if (key === 'api') {
        return 'api';
    }

    if (key === 'database') {
        return 'database';
    }

    if (key === 'payments') {
        return 'payments';
    }

    if (key === 'messaging' || key === 'sms') {
        return 'messaging';
    }

    if (key === 'email') {
        return 'email';
    }

    return 'web-app';
};

const overallLabel = computed(() => {
    if (!status.value) {
        return copy.value.checkingProduction;
    }

    return copy.value.overallLabels[status.value.summary.status] ?? copy.value.overallLabels.unknown;
});

const overallClass = computed(() => status.value?.summary.status ?? 'unknown');

const overallIcon = computed(() => statusIcons[status.value?.summary.status] ?? statusIcons.unknown);

const generatedAt = computed(() => status.value?.page.generated_at);

const checkInterval = computed(() => status.value?.summary.check_interval_seconds ?? 60);

const formattedCheckedTime = computed(() => {
    if (!generatedAt.value) {
        return copy.value.checkingNow;
    }

    try {
        return new Intl.DateTimeFormat(currentLocale.value, {
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            timeZone: status.value?.page.timezone,
        }).format(new Date(generatedAt.value));
    } catch {
        return new Date(generatedAt.value).toLocaleTimeString();
    }
});

const formatMaintenanceWindow = (maintenance) => {
    const options = {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: status.value?.page.timezone,
    };

    try {
        const formatter = new Intl.DateTimeFormat(currentLocale.value, options);

        const startsAt = formatter.format(new Date(maintenance.starts_at));

        if (!maintenance.ends_at) {
            return `${startsAt} - ${copy.value.maintenanceNoEnd}`;
        }

        return `${startsAt} - ${formatter.format(new Date(maintenance.ends_at))}`;
    } catch {
        const startsAt = new Date(maintenance.starts_at).toLocaleString();

        if (!maintenance.ends_at) {
            return `${startsAt} - ${copy.value.maintenanceNoEnd}`;
        }

        return `${startsAt} - ${new Date(maintenance.ends_at).toLocaleString()}`;
    }
};

const maintenanceMessage = (maintenance) => maintenance.message?.[currentLocale.value]
    || maintenance.message?.en
    || '';

const relativeCheckedTime = computed(() => {
    if (!generatedAt.value) {
        return copy.value.checkingNow;
    }

    const seconds = Math.max(0, Math.round((Date.now() - new Date(generatedAt.value).getTime()) / 1000));

    if (seconds < 60) {
        return copy.value.lastCheckedLessThanMinute;
    }

    const minutes = Math.round(seconds / 60);

    return copy.value.lastCheckedMinutes(minutes);
});

const setPageMetadata = () => {
    document.documentElement.lang = currentLocale.value;
    document.title = copy.value.title;

    const metaDescription = document.querySelector('meta[name="description"]');

    if (metaDescription) {
        metaDescription.setAttribute('content', copy.value.metaDescription);
    }
};

const selectLocale = (locale) => {
    if (!supportedLocales.includes(locale)) {
        return;
    }

    currentLocale.value = locale;
    const url = new URL(window.location.href);
    url.searchParams.set('lang', locale);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};

const fetchStatus = async () => {
    isRefreshing.value = true;
    errorMessage.value = '';

    try {
        const response = await fetch('/api/status', {
            headers: {
                Accept: 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(copy.value.loadError);
        }

        status.value = await response.json();
    } catch (error) {
        errorMessage.value = error.message;
    } finally {
        isLoading.value = false;
        isRefreshing.value = false;
    }
};

onMounted(() => {
    setPageMetadata();
    fetchStatus();
    refreshTimer = window.setInterval(fetchStatus, checkInterval.value * 1000);
});

watch(currentLocale, setPageMetadata);

onUnmounted(() => {
    if (refreshTimer) {
        window.clearInterval(refreshTimer);
    }
});
</script>

<template>
    <main class="status-shell">
        <svg class="icon-symbols" aria-hidden="true">
            <symbol id="status-icon-website" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="8.4" />
                <path d="M3.8 12h16.4M12 3.6c2.3 2.3 3.4 5.1 3.4 8.4s-1.1 6.1-3.4 8.4M12 3.6C9.7 5.9 8.6 8.7 8.6 12s1.1 6.1 3.4 8.4" />
            </symbol>
            <symbol id="status-icon-web-app" viewBox="0 0 24 24">
                <path d="M5 6.5h14A1.5 1.5 0 0 1 20.5 8v8A1.5 1.5 0 0 1 19 17.5H5A1.5 1.5 0 0 1 3.5 16V8A1.5 1.5 0 0 1 5 6.5Z" />
                <path d="M3.8 10h16.4M7.2 8.3h.1M9.4 8.3h.1M11.6 8.3h.1" />
            </symbol>
            <symbol id="status-icon-api" viewBox="0 0 24 24">
                <path d="m8.5 8-4 4 4 4M15.5 8l4 4-4 4M13.5 5.5l-3 13" />
            </symbol>
            <symbol id="status-icon-database" viewBox="0 0 24 24">
                <path d="M5 7c0-1.7 3.1-3 7-3s7 1.3 7 3-3.1 3-7 3-7-1.3-7-3Z" />
                <path d="M5 7v5c0 1.7 3.1 3 7 3s7-1.3 7-3V7M5 12v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5" />
            </symbol>
            <symbol id="status-icon-payments" viewBox="0 0 24 24">
                <path d="M12 3v18M16.8 7.2c-.9-1-2.3-1.6-4.2-1.6-2.4 0-4.1 1.2-4.1 3.1 0 4.5 8.8 2 8.8 6.8 0 1.9-1.8 3.1-4.6 3.1-2.1 0-3.8-.7-5-2" />
            </symbol>
            <symbol id="status-icon-email" viewBox="0 0 24 24">
                <path d="M4.5 7.2h15A1.5 1.5 0 0 1 21 8.7v8.6a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.3V8.7a1.5 1.5 0 0 1 1.5-1.5Z" />
                <path d="m4.2 8 7.8 5.7L19.8 8" />
            </symbol>
            <symbol id="status-icon-messaging" viewBox="0 0 24 24">
                <path d="M7.2 17.8 4 20v-4.6A6.6 6.6 0 0 1 3 12c0-4 3.8-7.2 8.5-7.2S20 8 20 12s-3.8 7.2-8.5 7.2c-1.6 0-3.1-.4-4.3-1.4Z" />
                <path d="M8 11.5h6.8M8 14.2h4.5" />
            </symbol>
        </svg>

        <section class="hero" :class="overallClass">
            <div class="ambient ambient-pink"></div>
            <div class="ambient ambient-blue"></div>
            <div class="ambient ambient-blue-soft"></div>
            <div class="ambient-ring"></div>
            <div class="ambient-dot"></div>

            <div class="hero-inner">
                <div class="language-toggle" role="group" :aria-label="copy.languageToggleLabel">
                    <button
                        type="button"
                        :class="{ active: currentLocale === 'en' }"
                        :aria-pressed="currentLocale === 'en'"
                        @click="selectLocale('en')"
                    >
                        EN
                    </button>
                    <button
                        type="button"
                        :class="{ active: currentLocale === 'fr' }"
                        :aria-pressed="currentLocale === 'fr'"
                        @click="selectLocale('fr')"
                    >
                        FR
                    </button>
                </div>

                <div class="brand-logo-wrap">
                    <img :src="logoUrl" alt="ActivityMessenger">
                </div>

                <div class="hero-copy">
                    <h1>{{ copy.title }}</h1>
                    <div class="status-pill">
                        <span class="check-icon">{{ overallIcon }}</span>
                        <span>{{ overallLabel }}</span>
                    </div>
                </div>
            </div>
        </section>

        <section v-if="isLoading" class="notice">{{ copy.checkingProduction }}</section>
        <section v-else-if="errorMessage" class="notice error">{{ errorMessage }}</section>

        <section v-else-if="status" class="dashboard-grid">
            <div class="side-column">
                <article class="panel overview-panel">
                    <h2>{{ copy.currentOverview }}</h2>
                    <div class="overview-grid">
                        <div class="metric-box">
                            <span class="metric-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24">
                                    <path d="M5 5.8h14M5 12h14M5 18.2h14" />
                                    <path d="M8 5.8v12.4M16 5.8v12.4" />
                                </svg>
                            </span>
                            <span>{{ copy.servicesOperational }}</span>
                            <strong>{{ status.summary.operational_checks }} / {{ status.summary.total_checks }}</strong>
                        </div>
                        <div class="metric-box">
                            <span class="metric-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="8.4" />
                                    <path d="M12 7.6v4.7l3.1 1.9" />
                                </svg>
                            </span>
                            <span>{{ copy.checkInterval }}</span>
                            <strong>{{ status.summary.check_interval_seconds }} {{ copy.secondsUnit }}</strong>
                        </div>
                        <div class="metric-box">
                            <span class="metric-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24">
                                    <path d="M4 14.4a8 8 0 1 1 16 0" />
                                    <path d="m12 13.2 4.2-4.2" />
                                    <path d="M6.3 17.8h11.4" />
                                </svg>
                            </span>
                            <span>{{ copy.averageResponse }}</span>
                            <strong>{{ status.summary.average_response_time_ms }} ms</strong>
                        </div>
                        <div class="metric-box">
                            <span class="metric-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="8.2" />
                                    <path d="M12 7.7v4.6l3.2 1.9" />
                                </svg>
                            </span>
                            <span>{{ copy.lastChecked }}</span>
                            <strong>{{ formattedCheckedTime }}</strong>
                        </div>
                    </div>
                </article>
            </div>

            <article v-if="hasMaintenances" class="panel maintenance-panel">
                <section v-if="activeMaintenances.length" class="maintenance-group active">
                    <header class="maintenance-group-header">
                        <h2>{{ copy.maintenanceActive }}</h2>
                    </header>
                    <div class="maintenance-list">
                        <div v-for="maintenance in activeMaintenances" :key="maintenance.id" class="maintenance-row">
                            <span class="maintenance-status-dot active" aria-hidden="true"></span>
                            <div class="maintenance-content">
                                <strong>{{ maintenance.title }}</strong>
                                <p v-if="maintenanceMessage(maintenance)" class="maintenance-message">
                                    {{ maintenanceMessage(maintenance) }}
                                </p>
                                <div class="maintenance-meta">
                                    <span>
                                        <b>{{ copy.maintenanceWindow }}</b>
                                        {{ formatMaintenanceWindow(maintenance) }}
                                    </span>
                                </div>
                                <div class="maintenance-service-chips" :aria-label="copy.maintenanceServices">
                                    <span
                                        v-for="service in maintenance.affected_services"
                                        :key="`${maintenance.id}-${service}`"
                                        class="maintenance-service-chip"
                                    >
                                        <svg viewBox="0 0 24 24" aria-hidden="true">
                                            <use :href="`#status-icon-${serviceIcon(service)}`"></use>
                                        </svg>
                                        {{ displayServiceName(service) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section v-if="upcomingMaintenances.length" class="maintenance-group">
                    <header class="maintenance-group-header">
                        <h2>{{ copy.maintenanceUpcoming }}</h2>
                    </header>
                    <div class="maintenance-list">
                        <div v-for="maintenance in upcomingMaintenances" :key="maintenance.id" class="maintenance-row">
                            <span class="maintenance-status-dot" aria-hidden="true"></span>
                            <div class="maintenance-content">
                                <strong>{{ maintenance.title }}</strong>
                                <p v-if="maintenanceMessage(maintenance)" class="maintenance-message">
                                    {{ maintenanceMessage(maintenance) }}
                                </p>
                                <div class="maintenance-meta">
                                    <span>
                                        <b>{{ copy.maintenanceWindow }}</b>
                                        {{ formatMaintenanceWindow(maintenance) }}
                                    </span>
                                </div>
                                <div class="maintenance-service-chips" :aria-label="copy.maintenanceServices">
                                    <span
                                        v-for="service in maintenance.affected_services"
                                        :key="`${maintenance.id}-${service}`"
                                        class="maintenance-service-chip"
                                    >
                                        <svg viewBox="0 0 24 24" aria-hidden="true">
                                            <use :href="`#status-icon-${serviceIcon(service)}`"></use>
                                        </svg>
                                        {{ displayServiceName(service) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </article>

            <article class="panel live-services">
                <header class="panel-header">
                    <h2>{{ copy.liveServices }}</h2>
                    <button class="refresh-button" type="button" :disabled="isRefreshing" @click="fetchStatus">
                        {{ isRefreshing ? copy.refreshing : copy.refresh }}
                    </button>
                </header>

                <div class="service-list">
                    <div v-for="check in liveServices" :key="check.id" class="service-row" :class="check.status">
                        <span class="service-icon" :class="serviceIcon(check.name)" aria-hidden="true">
                            <svg viewBox="0 0 24 24" role="img">
                                <use :href="`#status-icon-${serviceIcon(check.name)}`"></use>
                            </svg>
                        </span>
                        <span class="service-name">
                            <strong>{{ displayServiceName(check.name) }}</strong>
                        </span>
                        <span class="service-status">
                            <span class="mini-check">{{ statusIcons[check.status] ?? statusIcons.unknown }}</span>
                            {{ copy.statusLabels[check.status] ?? copy.statusLabels.unknown }}
                        </span>
                    </div>
                </div>

                <footer class="panel-foot">
                    <span class="clock-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="8.2" />
                            <path d="M12 7.6v4.7l3.2 1.9" />
                        </svg>
                    </span>
                    <span>{{ relativeCheckedTime }}</span>
                </footer>
            </article>
        </section>
    </main>
</template>
